import { useState, useCallback } from 'react';
import { useFolderContext } from '../context/FolderContext';
import { useAuth } from '../context/AuthContext';
import { 
  validateFolderName, 
  validateFolderDescription, 
  getFriendlyErrorMessage,
  canMoveFolder 
} from '../utils/folderUtils';

/**
 * Custom hook for folder operations with validation and error handling
 */
export const useFolderOperations = () => {
  const { user } = useAuth();
  const {
    createFolder: contextCreateFolder,
    updateFolder: contextUpdateFolder,
    deleteFolder: contextDeleteFolder,
    moveFolder: contextMoveFolder,
    folderTree,
    isRootFolder
  } = useFolderContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Create folder with validation
  const createFolder = useCallback(async (folderData, parentId = null) => {
    try {
      setLoading(true);
      setError(null);

      // Validate folder name
      const nameValidation = validateFolderName(folderData.name);
      if (!nameValidation.isValid) {
        throw new Error(nameValidation.error);
      }

      // Validate description if provided
      if (folderData.description) {
        const descValidation = validateFolderDescription(folderData.description);
        if (!descValidation.isValid) {
          throw new Error(descValidation.error);
        }
      }

      const result = await contextCreateFolder(folderData, parentId);
      return result;
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err);
      setError(friendlyMessage);
      throw new Error(friendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [contextCreateFolder]);

  // Update folder with validation and root folder restrictions
  const updateFolder = useCallback(async (folderId, updateData) => {
    try {
      setLoading(true);
      setError(null);

      // Validate name if being updated
      if (updateData.name !== undefined) {
        const nameValidation = validateFolderName(updateData.name);
        if (!nameValidation.isValid) {
          throw new Error(nameValidation.error);
        }
      }

      // Validate description if being updated
      if (updateData.description !== undefined) {
        const descValidation = validateFolderDescription(updateData.description);
        if (!descValidation.isValid) {
          throw new Error(descValidation.error);
        }
      }

      // Check root folder restrictions
      if (isRootFolder(folderId)) {
        const restrictedFields = ['parentId', 'parentFolderId'];
        const hasRestrictedFields = restrictedFields.some(field => 
          updateData.hasOwnProperty(field)
        );
        
        if (hasRestrictedFields) {
          throw new Error('Cannot move your main workspace folder');
        }
      }

      const result = await contextUpdateFolder(folderId, updateData);
      return result;
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err);
      setError(friendlyMessage);
      throw new Error(friendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [contextUpdateFolder, isRootFolder]);

  // Delete folder with root folder protection
  const deleteFolder = useCallback(async (folderId) => {
    try {
      setLoading(true);
      setError(null);

      // Check if trying to delete root folder
      if (isRootFolder(folderId)) {
        throw new Error('Cannot delete your main workspace folder');
      }

      await contextDeleteFolder(folderId);
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err);
      setError(friendlyMessage);
      throw new Error(friendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [contextDeleteFolder, isRootFolder]);

  // Move folder with validation and restrictions
  const moveFolder = useCallback(async (folderId, newParentId) => {
    try {
      setLoading(true);
      setError(null);

      // Validate move operation
      const moveValidation = canMoveFolder(folderId, newParentId, folderTree, user?.userId);
      if (!moveValidation.canMove) {
        throw new Error(moveValidation.error);
      }

      await contextMoveFolder(folderId, newParentId);
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err);
      setError(friendlyMessage);
      throw new Error(friendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [contextMoveFolder, folderTree, user?.userId]);

  // Rename folder (convenience method)
  const renameFolder = useCallback(async (folderId, newName) => {
    return updateFolder(folderId, { name: newName });
  }, [updateFolder]);

  // Update folder description (convenience method)
  const updateFolderDescription = useCallback(async (folderId, description) => {
    return updateFolder(folderId, { description });
  }, [updateFolder]);

  // Batch operations
  const batchCreateFolders = useCallback(async (foldersData, parentId = null) => {
    const results = [];
    const errors = [];

    for (const folderData of foldersData) {
      try {
        const result = await createFolder(folderData, parentId);
        results.push(result);
      } catch (err) {
        errors.push({ folderData, error: err.message });
      }
    }

    return { results, errors };
  }, [createFolder]);

  return {
    // State
    loading,
    error,
    
    // Actions
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    renameFolder,
    updateFolderDescription,
    batchCreateFolders,
    clearError,
    
    // Utilities
    validateFolderName,
    validateFolderDescription,
    canMoveFolder: (folderId, targetParentId) => 
      canMoveFolder(folderId, targetParentId, folderTree, user?.userId)
  };
};

export default useFolderOperations;
